import React, { useEffect, useState } from 'react';
import moment from 'moment';

import Box from '@mui/material/Box';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import Grid from '@mui/material/Grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import Drawer from '../shared/Drawer';
import Autocomplete from '../shared/Autocomplete';
import Button from '../shared/Button';
import Search from '../shared/Search';

import userRoles, { companyRoles } from '../../constants/userRoles';
import { numberOfSteps } from '../../constants/cases';

const statusesOptions = ['Open', 'Completed', 'Deleted', 'Postponed'];

const steps = [...Array(numberOfSteps).keys()].map((i) => ({
  label: `Step ${i + 1}`,
  value: i + 1,
}));

const dateTypeOptions = [
  { label: 'All', value: 'all' },
  { label: 'With Date', value: 'withDate' },
  { label: 'TBC', value: 'tbc' },
];

const rolesWithCompanyFilter = [
  userRoles.GLOBAL_ADMIN.name,
  // userRoles.SURGEON.name,
  userRoles.ENGINEER.name,
  userRoles.MANUFACTURER.name,
  userRoles.LOGISTICS.name,
];

const datePickerTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: '8px',
    background: 'var(--color-surface)',
    '& fieldset': {
      borderColor: 'var(--color-border)',
    },
    '&:hover fieldset': {
      borderColor: 'var(--color-border)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--color-main)',
      borderWidth: 1,
    },
  },
  '& .MuiInputBase-input': {
    fontSize: 14,
    color: 'var(--color-text-primary)',
    padding: '8px 8px',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'var(--color-text-muted)',
    opacity: 1,
  },
  '& .MuiSvgIcon-root': {
    color: 'var(--color-text-muted)',
  },
};

const FilterCasesModal = (props) => {
  const {
    filter,
    open,
    onSubmit,
    onClose,
    hospitals = [],
    countries = [],
    procedures = [],
    surgeons = [],
    companies = [],
    search = '',
    onSearchChange = () => {},
    // teams = [],
    userRole,
  } = props;

  const [selectedStatuses, setSelectedStatuses] = useState(['Open']);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedHospitals, setSelectedHospitals] = useState([]);
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [selectedSurgeons, setSelectedSurgeons] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedSteps, setSelectedSteps] = useState([]);
  const [selectedDateType, setSelectedDateType] = useState(dateTypeOptions[0]);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  useEffect(() => {
    if (filter) {
      setSelectedStatuses(filter?.statuses || []);
      setSelectedCountries(filter?.countries || []);
      setSelectedHospitals(filter?.hospitals || []);
      setSelectedProcedures(filter?.procedures || []);
      setSelectedSurgeons(filter?.surgeons || []);
      setSelectedCompanies(filter?.companies?.length ? filter.companies.map((companyId) => companies.find((company) => company.id === companyId)).filter(Boolean) : []);
      setSelectedSteps(filter?.steps || []);
      setSelectedDateType(dateTypeOptions.find((option) => option.value === filter?.dateType) || dateTypeOptions[0]);
      setDateFrom(filter?.date?.from ? moment(filter.date.from) : null);
      setDateTo(filter?.date?.to ? moment(filter.date.to) : null);
    }
  }, [open, filter, hospitals, countries, procedures, surgeons, companies]);

  const onClear = () => {
    setSelectedCountries([]);
    setSelectedHospitals([]);
    setSelectedProcedures([]);
    setSelectedSurgeons([]);
    setSelectedCompanies([]);
    setSelectedSteps([]);
    setSelectedDateType(dateTypeOptions[0]);
    setDateFrom(null);
    setDateTo(null);
  };

  const handleSubmit = () => {
    onSubmit({
      countries: selectedCountries,
      hospitals: selectedHospitals,
      procedures: selectedProcedures,
      surgeons: selectedSurgeons,
      companies: selectedCompanies.map((company) => company.id),
      statuses: selectedStatuses,
      steps: selectedSteps,
      dateType: selectedDateType?.value || 'all',
      date: dateFrom || dateTo ? { from: dateFrom, to: dateTo } : null,
    });
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title="Filter Cases"
      subtitle="Apply filters to find specific cases"
      submitText="Submit"
    >
      <Box sx={{ minWidth: { xs: 'auto', md: '560px' }, maxWidth: '560px' }}>
        <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 2 }}>
          <Search
            value={search}
            placeholder="Search by Case ID"
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </Box>
        <Box>
          <Autocomplete
            value={selectedStatuses}
            onChange={setSelectedStatuses}
            options={statusesOptions}
            label="Status"
            multiple
          />
        </Box>
        {rolesWithCompanyFilter.includes(userRole) && (
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              value={selectedCompanies}
              onChange={setSelectedCompanies}
              options={companies}
              label="Company"
              getOptionLabel={(option) => option?.name || ''}
              multiple
            />
          </Box>
        )}
        {(userRole === userRoles.GLOBAL_ADMIN.name || companyRoles.includes(userRole)) && (
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              value={selectedCountries}
              onChange={setSelectedCountries}
              options={countries}
              label="Country"
              multiple
            />
          </Box>
        )}
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            value={selectedHospitals}
            onChange={setSelectedHospitals}
            options={hospitals}
            label="Hospital"
            multiple
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            value={selectedProcedures}
            onChange={setSelectedProcedures}
            options={procedures}
            label="Procedure"
            multiple
          />
        </Box>
        {userRole !== userRoles.SURGEON.name && (
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              value={selectedSurgeons}
              onChange={setSelectedSurgeons}
              options={surgeons}
              label="Surgeon"
              multiple
            />
          </Box>
        )}
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            value={selectedSteps}
            onChange={setSelectedSteps}
            options={steps}
            label="Step"
            multiple
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Box sx={(theme) => ({ fontSize: 14, color: theme.palette.text.secondary, mb: 1 })}>
            Surgery Date
          </Box>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ fontSize: 14, mb: '2px' }}>From</Box>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={dateFrom}
                  onChange={(date) => setDateFrom(date)}
                  maxDate={dateTo || undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      size: 'small',
                      placeholder: 'Select date',
                      sx: datePickerTextFieldSx,
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ fontSize: 14, mb: '2px' }}>To</Box>
                <DatePicker
                  format="DD/MM/YYYY"
                  value={dateTo}
                  onChange={(date) => setDateTo(date)}
                  minDate={dateFrom || undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      size: 'small',
                      placeholder: 'Select date',
                      sx: datePickerTextFieldSx,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Box>
        <Box sx={{ mt: 1 }}>
          <Autocomplete
            value={selectedDateType}
            onChange={(value) => setSelectedDateType(value || dateTypeOptions[0])}
            options={dateTypeOptions}
            label="Surgery Date Type"
            getOptionLabel={(option) => option?.label || ''}
          />
        </Box>

        <Button
          sx={{ mt: 2 }}
          text="Clear Filters"
          icon={<FilterAltOffOutlinedIcon />}
          onClick={onClear}
          type='secondary'
        />
      </Box>
    </Drawer>
  );
};

export default FilterCasesModal;
